import { DateTime } from 'luxon';
import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { locateItemVertically } from '../functions';
import { RootState } from '../store';
import { setterCalendar } from '../store/slices/calendarSlice';
import { setterGantItems } from '../store/slices/gantItemsSlice';
import { setterGantScroll } from '../store/slices/gantScrollSlice';
import { setterGant } from '../store/slices/gantSlice';
import { RoutingState } from '../store/slices/routingSlice';
import {
    HeaderType,
    ItemTaskType,
    ItemType,
    MappingItemIdHideCollapse,
    MappingItemIdIndexType,
    ModeTimeLineType,
} from '../types';
import { useRouting } from './routingProvider';

const filterUndefined = <T extends Record<string, any>>(obj: T): Partial<T> => {
    const result: Partial<T> = {};
    for (const [key, value] of Object.entries(obj)) {
        if (value !== undefined) {
            result[key as keyof T] = value;
        }
    }
    return result;
};

let isExecuted = false;

export const useApplyUrlParamsToRedux = (run: boolean) => {
    const dispatch = useDispatch();

    const routing = useSelector<RootState, RoutingState>((state) => state.routing as unknown as RoutingState);
    const mappingItemIdIndex = useSelector<RootState, MappingItemIdIndexType>((state) => state.gant.mappingItemIdIndex);
    const itemsList = useSelector<RootState, ItemType[]>((state) => state.gantItems.itemsList);
    const mappingItemIdHideCollapse = useSelector<RootState, MappingItemIdHideCollapse>(
        (state) => state.gant.mappingItemIdHideCollapse,
    );
    const tasks = useSelector<RootState, ItemTaskType[] | null>((state) => state.calendar.processedTasksFromServer);
    const startDateExtend = useSelector<RootState, string>((state) => state.gant.startDateExtend);
    const availableHeight = useSelector<RootState, number>((state) => state.calendar.availableHeight);

    const isReady = useRef(false);

    const { getUrlPath } = useRouting();

    useEffect(() => {
        if (!run || !routing.initial || isExecuted) {
            return;
        }
        isExecuted = true;

        const isGantPage = getUrlPath().endsWith('timeline');
        if (isGantPage && !DateTime.fromISO(startDateExtend).isValid) {
            return;
        }

        const url = new URL(routing.initial.url);

        const item = url.searchParams.get('item') ?? undefined;
        const isWaitedForItems = !item || itemsList?.length > 0;

        const task = url.searchParams.get('task') ?? undefined;
        const isWaitedForTasks = !task || tasks;

        if (isWaitedForItems && isWaitedForTasks) {
            const currentDate = url.searchParams.get('cldd') ?? undefined;
            const scheduleScale = (url.searchParams.get('clds') as HeaderType) ?? undefined;
            const headerType = (url.searchParams.get('period') as HeaderType) ?? undefined;
            const modeTimeLine = (url.searchParams.get('mode') as ModeTimeLineType) ?? undefined;

            const currentGantItem = item && itemsList ? { ...itemsList[mappingItemIdIndex[item]] } : undefined;
            const currentTask = task && tasks ? tasks.find((taskItem) => taskItem.id === task) : undefined;

            const { newItemsList, newScrollY, newMappingItemIdHideCollapse } = currentGantItem
                ? locateItemVertically(
                      currentGantItem,
                      itemsList,
                      mappingItemIdIndex,
                      mappingItemIdHideCollapse,
                      availableHeight,
                  )
                : { newItemsList: undefined, newScrollY: 0, newMappingItemIdHideCollapse: undefined };

            dispatch(
                setterGantItems(
                    filterUndefined({
                        currentGantItem,
                        isItemEdit: Boolean(currentGantItem),
                        ...newItemsList,
                    }),
                ),
            );

            dispatch(
                setterGantScroll({
                    currentScrollY: newScrollY,
                }),
            );

            dispatch(
                setterGant(
                    filterUndefined({
                        headerType,
                        modeTimeLine,
                        mappingItemIdHideCollapse: newMappingItemIdHideCollapse,
                    }),
                ),
            );

            dispatch(
                setterCalendar(
                    filterUndefined({
                        currentDate,
                        scheduleScale,
                        currentTask,
                    }),
                ),
            );

            isReady.current = true;
        }
    }, [routing.initial, startDateExtend, run]);

    return isReady.current;
};
